import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { ConsentService } from '../consent/consent.service';
import { environment } from '../../../environments/environment';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Google Analytics 4.
 *
 * Loaded from here rather than pasted into index.html, because the pasted snippet is wrong for a
 * single-page app in a way that is easy to miss: it sends one `page_view` when the document loads
 * and never again. A reader moving from the home page to a store page to an article would appear in
 * GA4 as a single pageview on `/`, and every per-page report — landing pages, exit rates, content
 * groupings — would be built on that one row. The router navigations below are what make the rest
 * of the site visible.
 *
 * Off unless a measurement ID is configured, which is how development stays out of the production
 * property (see environment.ts). Never runs during server rendering.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly consent = inject(ConsentService);

  private readonly measurementId = environment.gaMeasurementId?.trim() ?? '';
  private enabled = false;

  private get gtag(): ((...args: any[]) => void) | null {
    const fn = (this.document.defaultView as any)?.gtag;
    return typeof fn === 'function' ? fn : null;
  }

  init(): void {
    if (!isPlatformBrowser(this.platformId) || !this.measurementId || this.enabled) return;
    this.enabled = true;

    const win = this.document.defaultView as any;
    win.dataLayer = win.dataLayer || [];
    // The real gtag is a shim that pushes `arguments` onto dataLayer — it has to stay a function
    // declaration for that, which is why this is not an arrow.
    win.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      win.dataLayer.push(arguments);
    };

    // Consent Mode defaults FIRST. A `consent default` that arrives after the config call is too
    // late — the tag has already decided what it may store, so the first page of every visit would
    // be measured as though consent had been given.
    this.consent.applyDefaults();

    win.gtag('js', new Date());
    win.gtag('config', this.measurementId, {
      // We send page_view ourselves, from the router. Left on, GA4 would send one for the initial
      // document AND we would send one for the same navigation — every entry counted twice.
      send_page_view: false,
    });

    this.loadWhenIdle();
    this.trackNavigations();
  }

  /**
   * Fetches gtag.js once the browser has finished the work the visitor is waiting on.
   *
   * The tag is 183 KB — the second largest thing the home page downloads, after the application
   * itself. `async` already kept it off the parser's critical path, but it still competed for
   * bandwidth and main thread with the first render, and on a throttled phone that is measurable in
   * both First Contentful Paint and Speed Index.
   *
   * Nothing is lost by waiting. `gtag()` is a shim that pushes onto `dataLayer`, so the consent
   * defaults and the config call above are already queued; the real tag drains that queue in order
   * the moment it arrives, and the events carry the timestamps they were recorded with. The only
   * change is when the file is fetched.
   *
   * `requestIdleCallback` with a deadline, so a browser that never goes idle — or one without it,
   * which is Safari before 17 — still loads the tag rather than never reporting at all.
   */
  /**
   * Fetch gtag.js ourselves ONLY if nothing else is going to.
   *
   * Google Tag Manager loads gtag.js for its own GA4 tag. When a container is on the page we were
   * therefore downloading the same 181 KB script twice — measured on a live store page as
   * `gtag/js?id=G-24TET7MQGV` and `gtag/js?id=G-24TET7MQGV&cx=c&gtm=…`, two separate 180.8 KB
   * transfers. Together with the container itself that put 474 KB of tag code on the page, 53% of
   * all the JavaScript it loads, and a large share of its Total Blocking Time.
   *
   * Skipping our copy loses nothing. `gtag()` here is a shim that pushes onto `dataLayer` (see
   * `init`), and GTM's tag drains that same queue in order the moment it arrives — consent
   * defaults, config and every event we have recorded, with the timestamps they were recorded with.
   *
   * The dependency this creates is worth stating plainly: with a container present, GA4 now arrives
   * through it. Remove the GA4 tag from the container and measurement stops, with nothing in this
   * code to indicate why.
   */
  private loadWhenIdle(): void {
    const win = this.document.defaultView as any;

    // The container snippet in index.html; GTM injects its own script tag from there.
    const hasTagManager = Boolean(
      this.document.querySelector('script[src*="googletagmanager.com/gtm.js"]') ||
        win?.google_tag_manager ||
        // The container is now injected on the reader's first interaction (see index.html), so
        // neither of the two checks above is true yet when this runs. Without this flag we would
        // decide no container exists and fetch gtag.js ourselves — the exact duplicate load, and
        // the doubled page views and click_outs, that this guard was added to prevent.
        win?.__savGtmDeferred,
    );
    if (hasTagManager) return;

    const load = () => {
      const script = this.document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(this.measurementId)}`;
      this.document.head.appendChild(script);
    };

    if (typeof win?.requestIdleCallback === 'function') win.requestIdleCallback(load, { timeout: 4000 });
    else win?.setTimeout(load, 2000);
  }

  private trackNavigations(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        // A macrotask later, so the page component's SeoService.apply() has set the title. Reading
        // it during NavigationEnd gets the PREVIOUS page's title, which mislabels every row in the
        // Pages report by one navigation.
        setTimeout(() => this.sendPageView(), 0);
      });
  }

  private sendPageView(): void {
    const win = this.document.defaultView;
    this.gtag?.('event', 'page_view', {
      page_title: this.document.title,
      page_location: win?.location.href,
      page_path: win ? win.location.pathname + win.location.search : undefined,
    });
  }

  /**
   * A visitor leaving for a merchant — the one event on a coupon site that maps to revenue.
   *
   * `transport_type: 'beacon'` is not optional here. The click-out sets `location.href` on the
   * current tab, so the page starts unloading immediately; an ordinary XHR is cancelled mid-flight
   * and the event is simply lost. sendBeacon is handed to the browser to deliver after the page is
   * gone, which is the only transport that survives a navigation away.
   */
  trackClickOut(details: { store: string; offerId?: string; kind?: string }): void {
    this.gtag?.('event', 'click_out', {
      store_name: details.store,
      offer_id: details.offerId ?? '',
      click_kind: details.kind ?? 'coupon',
      transport_type: 'beacon',
    });
  }

  /** Anything else worth counting later — search, follow, share — without touching this file. */
  track(eventName: string, params: Record<string, unknown> = {}): void {
    this.gtag?.('event', eventName, params);
  }
}
